import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Box, CircularProgress, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { APROBAR_LIQUIDACION_PROFESIONAL } from "../../../data/graphQL/mutations/LiquidacionFinal/aprobarLiquidacionProfesional";
import { DESESTIMAR_LIQUIDACION } from "../../../data/graphQL/mutations/LiquidacionFinal/desestimarLiquidacion";
import { VALIDAR_PRE_LIQUIDACION } from "../../../data/graphQL/queries/LiquidacionFinal/validarPreLiquidacion";
import { useProgressiveList } from "../../../hooks/useProgressiveList";
import { useGetRows } from "../hooks/useGetRows";
import { useSeleccionLiquidaciones } from "../hooks/useSeleccionLiquidaciones";
import { DialogDetalleLiquidacion } from "./Dialogs/DialogDetalleLiquidacion";
import { DialogLiquidacionesPendiente } from "./Dialogs/DialogLiquidacionesPendiente";
import { DialogRechazarLiquidacion } from "./Dialogs/DialogRechazarLiquidacion";
import { LiquidacionFinalGrillaWrapper } from "./LiquidacionFinalGrillaWrapper";
import { LiquidacionFinalHeader } from "./LiquidacionFinalHeader";
import { DialogConfirmacion } from "../../PreLiquidacion/components/Dialogs/DialogConfirmacionLiquidar";
import { PreLiquidacionFilters } from "../../PreLiquidacion/components/PreLiquidacionFilters";
import { useGetMontosLiquidacion } from "../../PreLiquidacion/hooks/useGetMontosLiquidacion";
import { useGetFilters } from "../../PreLiquidacion/hooks/useGetFilters";
import { useDebounce } from "../../../hooks/useDebounce";
import { filtrarLiquidacionesPorTexto } from "../../PreLiquidacion/utils/filterLiquidacionesPorTexto";

export const ID_ESTADO_LIQUIDACION_APROBADO = 4;
export const ID_ESTADO_LIQUIDACION_RECHAZO = 3;

export const LiquidacionFinalContent = ({ setChildrenAction }) => {
    const rutasStorage = localStorage.getItem('navigationRoutes');
    const location = useLocation();
    const scrollContainerRef = useRef(null);

    const [scrollHeight, setScrollHeight] = useState('auto');
    const [allowedWrite, setAllowedWrite] = useState(false);
    const [openDetalle, setOpenDetalle] = useState(false);
    const [openLiquidacionesPendientes, setOpenLiquidacionesPendientes] = useState(false);
    const [openRechazar, setOpenRechazar] = useState(false);
    const [openConfirmAprobar, setOpenConfirmAprobar] = useState(false);
    const [liquidacionSeleccionada, setLiquidacionSeleccionada] = useState(null);
    const [isRefetchRequired, setIsRefetchRequired] = useState(true);
    const [valorEsperado, setValorEsperado] = useState([]);

    const {
        filters,
        handleChangeFilters,
        delegaciones,
        profesionales,
        estados,
        tiposProfesional,
    } = useGetFilters(setIsRefetchRequired, true);

    const {
        handleSearch: searchLiquidaciones,
        liquidacionesPorProfesional,
        loading: loadingRows,
        busquedaCompletada
    } = useGetRows(filters, setIsRefetchRequired);

    const {
        montos,
        fetchMontos,
        loading: loadingMontos
    } = useGetMontosLiquidacion();

    const [validarPreLiquidacion] = useLazyQuery(VALIDAR_PRE_LIQUIDACION, { fetchPolicy: 'no-cache' });
    const [aprobarLiquidacionProfesional, { loading: loadingAprobacion }] = useMutation(APROBAR_LIQUIDACION_PROFESIONAL, { fetchPolicy: 'no-cache' });
    const [desestimarLiquidacion, { loading: loadingDesestimar }] = useMutation(DESESTIMAR_LIQUIDACION, { fetchPolicy: 'no-cache' });

    const refrescarTodo = () => {
        searchLiquidaciones();
        fetchMontos(filters);
    };

    const {
        toggleSeleccion,
        seleccionarTodos,
        limpiarSeleccion,
        cantidadSeleccionada,
        seleccionados
    } = useSeleccionLiquidaciones();

    const handleViewDetalle = (row) => {
        setLiquidacionSeleccionada(row);
        setOpenDetalle(true);
    };

    const handleClickAprobar = () => {
        if (cantidadSeleccionada === 0) return;
        setOpenConfirmAprobar(true);
    };

    const ejecutarAprobar = async (ids) => {
        const input = ids.map(id => {
            const registro = liquidacionesPorProfesional.find(r => r.idLiquidacionProfesional === id);
            const detalleCuenta = registro.liquidaciones.map(l => ({
                cuenta: l.cuenta,
                centroCosto: l.centroCosto,
                centroAlmacenamiento: l.centroAlmacenamiento,
                descripcionServicio: l.programa || '',
                liquidacionTotal: l.liquidacionTotal
            }));
            return {
                idLiquidacionProfesional: id,
                detalleCuenta: detalleCuenta
            };
        });
        await aprobarLiquidacionProfesional({ variables: { input } })
            .then(response => {
                const { success, error } = response.data.generarOrdenOC;

                if (error) {
                    toast.error(`Ocurrió un error al aprobar las liquidaciones: ${error}`);
                } else if (!success) {
                    toast.error('No se pudo aprobar ninguna liquidación de los profesionales seleccionados');
                } else if (success) {
                    toast.success(`${ids.length} liquidación(es) aprobada(s) correctamente`);
                    limpiarSeleccion();
                    refrescarTodo();
                }
            }).catch(err => {
                console.error('Error al aprobar liquidaciones:', err);
                toast.error('Ocurrió un error al aprobar las liquidaciones');
            });
    };

    const handleConfirmAprobar = async () => {
        const ids = Array.from(seleccionados);
        const pendientes = [];

        for (const id of ids) {
            const result = await validarPreLiquidacion({ variables: { idLiquidacionProfesional: id } });
            if (!result.data.validarPreLiquidacion.success) {
                pendientes.push(result.data.validarPreLiquidacion.valorEsperado);
            }
        }

        if (pendientes.length > 0) {
            setOpenConfirmAprobar(false);
            setValorEsperado(pendientes.flat());
            setOpenLiquidacionesPendientes(true);
        } else {
            await ejecutarAprobar(ids);
            setOpenConfirmAprobar(false);
        }
    };

    const handleConfirmConPendientes = async () => {
        await ejecutarAprobar(Array.from(seleccionados));
        setOpenLiquidacionesPendientes(false);
    };

    const handleClickRechazar = () => {
        if (cantidadSeleccionada === 0) return;
        setOpenRechazar(true);
    }

    const handleConfirmarRechazar = async (justificacion) => {
        const ids = Array.from(seleccionados);

        try {
            const response = await desestimarLiquidacion({
                variables: { idLiquidacionProfesional: ids, justificacion }
            });

            const success = response?.data?.rechazarLiquidacionFinal === true;

            if (success) {
                toast.success(`Se rechazaron las liquidaciones de ${ids.length} profesional(es) correctamente`);
            } else {
                toast.error('No se pudo rechazar ninguna liquidación de los profesionales seleccionados');
            }
        } catch (error) {
            console.error('Error al desestimar liquidaciones:', error);
            toast.error('Ocurrió un error al rechazar las liquidaciones');
        } finally {
            limpiarSeleccion();
            refrescarTodo();
            setOpenRechazar(false);
        }
    };

    const handleSearch = () => {
        refrescarTodo();
    };

    useEffect(() => {
        setChildrenAction?.(
            <LiquidacionFinalHeader
                mes={filters.mes}
                totalMesAnterior={!isRefetchRequired ? montos.totalMesAnterior : null}
                totalMesAnteAnterior={!isRefetchRequired ? montos.totalMesAnteAnterior : null}
                allowedWrite={allowedWrite}
                cantidadSeleccionada={cantidadSeleccionada}
                handleClickRechazar={handleClickRechazar}
                handleClickAprobar={handleClickAprobar}
            />
        );
    }, [
        filters.mes,
        montos.totalMesAnterior,
        montos.totalMesAnteAnterior,
        cantidadSeleccionada,
        setChildrenAction,
        allowedWrite,
        isRefetchRequired
    ]);

    useEffect(() => {
        //Permisos para escritura
        if (rutasStorage) {
            // Por ruta (el usuario debe tener permisos de escritura de la ruta)
            const rutas = JSON.parse(rutasStorage);
            const rutaQueSirve = location.pathname;
            const escrituraRuta = rutas?.find((ruta) => ruta.aplicaciones.find((app) => app.url == rutaQueSirve)
            )?.aplicaciones?.find((app) => app.url == rutaQueSirve).escritura;

            //Por estado (la liquidación no puede estar aprobada)
            const noLiqAprobada = filters.idEstado !== ID_ESTADO_LIQUIDACION_APROBADO;

            //Por mes (solo se permite escribir en el mes anterior al actual, o en meses anteriores)
            const prevMonth = moment().subtract(1, 'month');
            const isPreviousMonth =
                filters.mes === prevMonth.month() + 1 &&
                filters.anio === prevMonth.year();

            setAllowedWrite(escrituraRuta && noLiqAprobada && isPreviousMonth);
        }
    }, [rutasStorage, filters.idEstado, filters.mes, filters.anio, location.pathname]);

    const cargando = useMemo(() => (loadingRows || loadingMontos), [loadingRows, loadingMontos]);

    const debouncedFiltrar = useDebounce(filters.filtrar, 1000);

    const liquidacionesFiltradas = useMemo(() => (
        filtrarLiquidacionesPorTexto(liquidacionesPorProfesional, debouncedFiltrar)
    ), [debouncedFiltrar, liquidacionesPorProfesional]);

    const sinResultados = useMemo(() => {
        return (busquedaCompletada && !cargando && liquidacionesFiltradas.length === 0)
    }, [
        busquedaCompletada,
        cargando,
        liquidacionesFiltradas.length
    ]);

    useEffect(() => {
        const calcHeight = () => {
            if (scrollContainerRef.current) {
                const offsetTop = scrollContainerRef.current.getBoundingClientRect().top;
                const paddingMainContent = 60;
                setScrollHeight(`calc(100vh - ${offsetTop}px - ${paddingMainContent}px)`);
            }
        };

        calcHeight();
        window.addEventListener('resize', calcHeight);
        return () => window.removeEventListener('resize', calcHeight);
    }, []);

    const visibleLiquidaciones = useProgressiveList(liquidacionesFiltradas, 5);

    return (
        <div>
            <PreLiquidacionFilters
                filters={filters}
                handleChangeFilters={handleChangeFilters}
                handleSearch={handleSearch}
                delegacionesOptions={delegaciones}
                profesionalesOptions={profesionales}
                estadosOptions={estados}
                tiposProfesionalOptions={tiposProfesional}
            />

            <Box
                ref={scrollContainerRef}
                sx={{ height: scrollHeight, overflowY: 'auto', pr: 1, scrollbarColor: '#8b8b8b transparent', minHeight: 230 }}
            >
                {isRefetchRequired && !cargando && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant='body1' color='text.secondary'>
                            Haz click en "Buscar" para aplicar los nuevos filtros
                        </Typography>
                    </Box>
                )}

                {cargando && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={40} />
                    </Box>
                )}

                {!isRefetchRequired && sinResultados && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant='body1' color='text.secondary'>
                            No se encontraron liquidaciones para los filtros seleccionados.
                        </Typography>
                    </Box>
                )}


                {!cargando && !isRefetchRequired && visibleLiquidaciones.map(
                    ({ idProfesional, profesionalData, liquidaciones, idLiquidacionProfesional }, index) => (
                        <LiquidacionFinalGrillaWrapper
                            key={idLiquidacionProfesional}
                            idProfesionalLiquidacion={idLiquidacionProfesional}
                            profesionalData={profesionalData}
                            liquidaciones={liquidaciones}
                            mesLiquidado={filters.mes}
                            allowedWrite={allowedWrite}
                            seleccionados={seleccionados}
                            toggleSeleccion={toggleSeleccion}
                            seleccionarTodos={seleccionarTodos}
                            handleViewDetalle={handleViewDetalle}
                            showDivider={(index + 1) !== liquidacionesFiltradas.length}
                            mostrarOrdenCompra={filters.idEstado === ID_ESTADO_LIQUIDACION_APROBADO}
                        />
                    ))}
            </Box>

            <DialogDetalleLiquidacion
                open={openDetalle}
                setOpen={setOpenDetalle}
                liquidacion={liquidacionSeleccionada}
                mes={filters.mes}
                anio={filters.anio}
                idTipoProfesional={filters.idTipoProfesional}
            />

            <DialogLiquidacionesPendiente
                open={openLiquidacionesPendientes}
                setOpen={setOpenLiquidacionesPendientes}
                liquidacion={liquidacionSeleccionada}
                mes={filters.mes}
                anio={filters.anio}
                onConfirmar={handleConfirmConPendientes}
                profesionales={valorEsperado}
                loading={loadingAprobacion}
            />

            <DialogRechazarLiquidacion
                open={openRechazar}
                setOpen={setOpenRechazar}
                cantidadSeleccionada={cantidadSeleccionada}
                onConfirmar={handleConfirmarRechazar}
                loading={loadingDesestimar}
            />

            <DialogConfirmacion
                open={openConfirmAprobar}
                title={`¿Está seguro que desea aprobar las liquidaciones de ${cantidadSeleccionada} profesional(es)?`}
                onCancel={() => setOpenConfirmAprobar(false)}
                onAccept={handleConfirmAprobar}
                loading={loadingAprobacion}
            />
        </div>
    );
};